import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";

/**
 * PreviewModal — renders a draft wave OR a single question exactly as a
 * patron would see it on `/panelist/wave/<wid>`, but read-only.
 *
 * v2 changes (2026-07-02):
 *   • Modal is now a flex-column with a scrolling body + STICKY footer, so
 *     Back/Next/Close never get clipped when a question has many options.
 *   • Exclusive-option enforcement: clicking an exclusive option (e.g. "None
 *     of the above") in a multi-select now clears the other picks, and
 *     clicking any non-exclusive after that clears the exclusive one —
 *     mirrors the real runner in Panelist.jsx.
 *   • Option label is read from `o.text` with a fallback to `o.label` so
 *     mixed-shape option arrays (from ORBIT / imports / hand-edited) still
 *     render correctly.
 */
export default function PreviewModal({ open, onClose, mode, waveId, question }) {
  const [wave, setWave] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [step, setStep] = useState(-1);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(mode === "wave" ? -1 : 0);
    setAnswers({});
    if (mode === "question" && question) {
      setWave(null);
      setQuestions([question]);
      return;
    }
    if (mode === "wave" && waveId) {
      setLoading(true);
      Promise.all([
        api.get(`/waves/${waveId}`),
        api.get("/questions"),
      ]).then(([wR, qR]) => {
        const w = wR.data;
        const qmap = new Map((qR.data || []).map((q) => [q.id, q]));
        const ordered = (w.question_ids || []).map((id) => qmap.get(id)).filter(Boolean);
        setWave(w);
        setQuestions(ordered);
      }).finally(() => setLoading(false));
    }
  }, [open, mode, waveId, question]);

  if (!open) return null;
  const total = questions.length;
  const q = step >= 0 && step < total ? questions[step] : null;

  const setAns = (val) => setAnswers((a) => ({ ...a, [q.id]: val }));
  const next = () => setStep((s) => s + 1);
  const prev = () => setStep((s) => Math.max(-1, s - 1));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      {/* Flex-column layout: header (fixed) · body (scroll) · footer (sticky).
          max-h-[92vh] guarantees the footer stays visible on any viewport. */}
      <DialogContent
        className="max-w-2xl max-h-[92vh] p-0 rounded-none border-2 border-black flex flex-col"
        data-testid="preview-modal"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-neutral-200 shrink-0">
          <DialogTitle className="text-2xl font-black tracking-tight flex items-center gap-2">
            <span className="bg-amber-200 text-black border-2 border-black px-2 py-0.5 text-[10px] font-bold tracking-wider">PREVIEW</span>
            <span className="truncate">{mode === "wave" ? (wave?.name || "Wave preview") : "Question preview"}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-4">
          <div className="text-[11px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-300 p-2 mt-3">
            ⚠ Read-only preview. Nothing is submitted, no points awarded.
          </div>

          {loading && <div className="text-neutral-500 text-sm py-8 text-center">Loading…</div>}

          {!loading && mode === "wave" && step === -1 && wave && (
            <div className="py-4 space-y-3">
              <div className="overline">BEFORE YOU START</div>
              <div className="text-xl font-bold">{wave.intro_topic || wave.name}</div>
              <p className="text-sm text-neutral-700 whitespace-pre-wrap">{wave.intro_text || "Tap Continue to begin the survey."}</p>
              <div className="text-xs text-neutral-500">
                ~{wave.expected_minutes || 3} minutes · {total} question{total === 1 ? "" : "s"} · earn {wave.points_award || 50} points
              </div>
            </div>
          )}

          {!loading && q && (
            <div className="py-4 space-y-4">
              {mode === "wave" && (
                <div className="text-xs text-neutral-500 font-mono">Question {step + 1} of {total}</div>
              )}
              <div className="text-lg font-bold">{q.text}</div>
              <QuestionRenderer q={q} answer={answers[q.id]} onChange={setAns} />
            </div>
          )}

          {!loading && mode === "wave" && step >= total && total > 0 && (
            <div className="py-8 text-center space-y-2">
              <div className="text-4xl">🎉</div>
              <div className="text-lg font-bold">End of preview</div>
              <div className="text-sm text-neutral-500">In production the patron would now see a thank-you screen with +{wave?.points_award || 50} points awarded.</div>
            </div>
          )}
        </div>

        {/* STICKY footer — never gets clipped by long option lists. */}
        <div className="shrink-0 flex items-center justify-between gap-3 px-6 py-3 border-t-2 border-black bg-white">
          <div className="text-xs text-neutral-500 truncate">
            {q?.visibility && <>Visibility: <span className="font-mono">{q.visibility}</span></>}
          </div>
          <div className="flex gap-2 shrink-0">
            {mode === "wave" && step > -1 && step <= total && (
              <Button variant="outline" onClick={prev} className="rounded-none" data-testid="preview-back">← Back</Button>
            )}
            {mode === "wave" && step < total && (
              <Button onClick={next} className="bg-black hover:bg-neutral-800 text-white rounded-none" data-testid="preview-next">
                {step === -1 ? "Continue" : step === total - 1 ? "Finish" : "Next"}
              </Button>
            )}
            <Button variant="outline" onClick={onClose} className="rounded-none" data-testid="preview-close">Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Read an option's display label — support both canonical `text` and legacy
// `label` (some import paths use one, some the other).
const optLabel = (o) => o?.text || o?.label || "";

function QuestionRenderer({ q, answer, onChange }) {
  const fmt = q.format || "single";
  const opts = q.options || [];
  if (fmt === "single") {
    return (
      <div className="space-y-2">
        {opts.map((o) => (
          <label
            key={o.key}
            className={`flex items-center gap-3 p-3 border-2 cursor-pointer ${answer === o.key ? "border-black bg-neutral-50" : "border-neutral-300 hover:border-black"}`}
          >
            <input type="radio" name={q.id} checked={answer === o.key} onChange={() => onChange(o.key)} />
            <span>{optLabel(o)}</span>
          </label>
        ))}
      </div>
    );
  }
  if (fmt === "multiple") {
    const set = new Set(answer || []);
    const exclusiveKeys = new Set(opts.filter((o) => o.exclusive).map((o) => o.key));
    const anyExclusive = exclusiveKeys.size > 0;
    const cap = q.max_selections || opts.length;

    const toggle = (o) => {
      const isExclusive = exclusiveKeys.has(o.key);
      // Mirror Panelist runner: exclusive click clears everything else
      // and stands alone; picking a non-exclusive clears any exclusive
      // choice first.
      let nextArr;
      if (isExclusive) {
        nextArr = set.has(o.key) ? [] : [o.key];
      } else {
        // Strip any exclusive selections when adding a real choice.
        const filtered = [...set].filter((k) => !exclusiveKeys.has(k));
        if (filtered.includes(o.key)) {
          nextArr = filtered.filter((k) => k !== o.key);
        } else {
          if (filtered.length >= cap) return; // cap reached, ignore
          nextArr = [...filtered, o.key];
        }
      }
      onChange(nextArr);
    };

    return (
      <div className="space-y-2">
        {opts.map((o) => (
          <label
            key={o.key}
            className={`flex items-center gap-3 p-3 border-2 cursor-pointer ${set.has(o.key) ? "border-black bg-neutral-50" : "border-neutral-300 hover:border-black"}`}
          >
            <input
              type="checkbox"
              checked={set.has(o.key)}
              onChange={() => toggle(o)}
            />
            <span className="flex-1">{optLabel(o)}</span>
            {o.exclusive && (
              <span className="text-[9px] font-mono uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-300 px-1.5 py-0.5">
                exclusive
              </span>
            )}
          </label>
        ))}
        <div className="text-[11px] text-neutral-500 flex items-center gap-2 pt-1">
          {q.max_selections && <span>Max {q.max_selections} selections.</span>}
          {anyExclusive && <span className="italic">Picking an "exclusive" option clears the rest.</span>}
        </div>
      </div>
    );
  }
  if (fmt === "nps") {
    return (
      <div className="flex flex-wrap gap-1.5">
        {Array.from({ length: 11 }, (_, i) => i).map((n) => (
          <button key={n} onClick={() => onChange(n)} className={`w-10 h-10 border-2 ${answer === n ? "bg-black text-white border-black" : "border-neutral-300 hover:border-black"}`}>{n}</button>
        ))}
      </div>
    );
  }
  if (fmt === "likert") {
    return (
      <div className="space-y-2">
        {["Strongly disagree", "Disagree", "Neutral", "Agree", "Strongly agree"].map((label, i) => (
          <label key={i} className={`flex items-center gap-3 p-2.5 border-2 cursor-pointer ${answer === i + 1 ? "border-black bg-neutral-50" : "border-neutral-300 hover:border-black"}`}>
            <input type="radio" name={q.id} checked={answer === i + 1} onChange={() => onChange(i + 1)} />
            <span>{label}</span>
          </label>
        ))}
      </div>
    );
  }
  return <div className="text-neutral-500 text-sm">Unsupported format: {fmt}</div>;
}
